import React, {useCallback, useEffect, useReducer, useRef, useState} from 'react';
import ReactDOM from 'react-dom';

import BenchlingConfig from 'BenchlingConfig';
import {SelectedParentStorable} from 'constants/SampleConstants';
import notifications from 'notifications';
import {
  listScratchPlateMappableColumns,
  MappableLayer,
  putScratchPlateInInventory,
  PutScratchPlateInInventoryParams,
  ScratchPlateColumnMapping,
} from 'plates/dao/ScratchPlateDAO';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import BarcodeInput from 'samples/BarcodeInput';
import BaseStorablePaneStudyableFormGroup from 'samples/BaseStorablePaneStudyableFormGroup';
import ParentStorableDataSelector from 'samples/ParentStorableDataSelector';
import PlateSchemaSelector from 'samples/PlateSchemaSelector';
import * as SampleSchemaActions from 'samples/SampleSchemaActions';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import {getEmptySelectedParent} from 'samples/storables/ParentStorableUtil';
import {PlateSchema, PlateSchemaType, PlateSize} from 'samples/storables/StorableSchemaConstants';
import {ConnectedStorableSourceSelector} from 'samples/storables/StorableSourceSelector';
import FormGroup from 'ui-library/bs/FormGroup';
import Modal, {ModalButton} from 'ui-library/modals/Modal';

import ScratchPlateColumnMappingTable from './ScratchPlateColumnMappingTable';

export interface PutScratchPlateInInventoryModalProps {
  scratchPlateId: string;
  scratchPlateName: string | null;
  plateSize: PlateSize;
  plateSchemaId: string | null;
  onRequestClose: () => void;
  onRequestSubmit: (result: {plateId: string; plateName: string}) => void;
}

function PutScratchPlateInInventoryModal({
  scratchPlateId,
  scratchPlateName,
  plateSize,
  plateSchemaId: initialPlateSchemaId,
  onRequestClose,
  onRequestSubmit,
}: PutScratchPlateInInventoryModalProps): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A successful submit calls onRequestSubmit, which unmounts this modal. The submit's
  // `finally` would then setState on an unmounted component, so guard against that.
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const [name, setName] = useState(scratchPlateName ?? '');
  const [shouldAutogenerateBarcode, setShouldAutogenerateBarcode] = useState(true);
  const [customBarcode, setCustomBarcode] = useState('');
  const [selectedPlateSchemaId, setSelectedPlateSchemaId] = useState<string | null>(initialPlateSchemaId);

  const [selectedParent, setSelectedParent] = useState<SelectedParentStorable>(getEmptySelectedParent);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [studyIds, setStudyIds] = useState<Array<string>>([]);

  const [mappableLayers, setMappableLayers] = useState<Array<MappableLayer>>([]);
  const [columnMappings, setColumnMappings] = useState<Array<ScratchPlateColumnMapping>>([]);

  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    SampleSchemaStore.addChangeListener(forceUpdate);
    // Container schemas hold the settable fields that columns map onto; ensure
    // they are loaded so selectedContainerSchema resolves once a schema is picked.
    SampleSchemaActions.fetchAllContainerSchemas();
    return () => {
      SampleSchemaStore.removeChangeListener(forceUpdate);
    };
  }, []);

  // The mappable source columns live on the scratch plate, independent of the
  // chosen plate schema, so fetch them once.
  useEffect(() => {
    let cancelled = false;
    listScratchPlateMappableColumns(scratchPlateId)
      .then((response) => {
        if (!cancelled) {
          setMappableLayers(response.layers);
        }
      })
      .catch(() => {
        // Column mapping is optional; failing to load source columns simply
        // hides the mapping table rather than blocking plate creation.
      });
    return () => {
      cancelled = true;
    };
  }, [scratchPlateId]);

  const selectedPlateSchema: PlateSchema | null =
    selectedPlateSchemaId != null ? SampleSchemaStore.getPlateSchema(selectedPlateSchemaId) : null;

  const selectedContainerSchema =
    selectedPlateSchema?.containerSchemaId != null
      ? SampleSchemaStore.getContainerSchema(selectedPlateSchema.containerSchemaId)
      : null;

  const plateSchemaFilter = useCallback(
    (schema: PlateSchema): boolean => schema.height === plateSize[0] && schema.width === plateSize[1],
    [plateSize]
  );

  const handlePlateSchemaChange = useCallback((schema: PlateSchema | null) => {
    setSelectedPlateSchemaId(schema?.id ?? null);
    // Mappings target a specific schema's fields, so drop them when the schema
    // changes rather than carrying over stale field references.
    setColumnMappings([]);
  }, []);

  const registryId = selectedPlateSchema?.registryId;

  const isDetailsValid = name.trim().length > 0 && selectedPlateSchemaId != null;
  const isProjectValid =
    !BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_NON_LOCATION_STORABLES ||
    (selectedProjectId != null && selectedProjectId !== registryId);
  const isLocationValid = selectedParent.storable != null;
  const canSubmit = isDetailsValid && isProjectValid && isLocationValid && !isSubmitting;

  async function handleSubmit(): Promise<void> {
    setIsSubmitting(true);
    try {
      const params: PutScratchPlateInInventoryParams = {
        scratchPlateId,
        plateSchemaId: selectedPlateSchemaId,
        name: name.trim() || undefined,
        barcodeValue: shouldAutogenerateBarcode ? undefined : customBarcode.trim() || undefined,
        sourceApiId: selectedProjectId ?? undefined,
        parentStorableApiId: selectedParent.storable?.id ?? undefined,
        columnMappings: columnMappings.length > 0 ? columnMappings : undefined,
        studyApiIds: studyIds.length > 0 ? studyIds : undefined,
      };

      const response = await putScratchPlateInInventory(params);
      notifications.success(`Plate "${response.plate.name}" created in inventory.`);
      onRequestSubmit({plateId: response.plate.id, plateName: response.plate.name});
    } catch (error: unknown) {
      if (error instanceof Error) {
        notifications.error(error.message);
      } else if (error != null && typeof error === 'object') {
        notifications.notifyError(error as Response);
      } else {
        throw error;
      }
    } finally {
      // Skip the state update if a successful submit already unmounted the modal.
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }

  const buttons: Array<ModalButton> = [
    {text: 'Cancel', cancel: true, style: 'default'},
    {text: isSubmitting ? 'Creating...' : 'Create', submit: true, style: 'primary', disabled: !canSubmit},
  ];

  return (
    <Modal
      title="Put plate in inventory"
      large={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={buttons}
    >
      <FormGroup>
        <label className="control-label">
          Plate name <span className="text-danger">*</span>
        </label>
        <input
          className="form-control"
          type="text"
          placeholder="Enter plate name..."
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          autoFocus={true}
        />
      </FormGroup>

      <FormGroup>
        <label className="control-label">
          Plate schema <span className="text-danger">*</span>
        </label>
        <p className="help-block">
          Only schemas matching the plate dimensions ({plateSize[0]} x {plateSize[1]}) are shown.
        </p>
        <PlateSchemaSelector
          plateSchemaType={PlateSchemaType.FIXED_PLATE}
          plateSchemaFilter={plateSchemaFilter}
          onChange={handlePlateSchemaChange}
          registryId={undefined}
          selectedPlateSchemaId={selectedPlateSchemaId}
          disabled={false}
          shouldShowNoSchemaOption={false}
        />
      </FormGroup>

      {selectedContainerSchema != null && mappableLayers.length > 0 && (
        <FormGroup>
          <label className="control-label">Column mapping</label>
          <p className="help-block">
            Map columns from the scratch plate onto fields on the new plate&apos;s wells.
          </p>
          <ScratchPlateColumnMappingTable
            schemaFields={selectedContainerSchema.fields}
            layers={mappableLayers}
            mappings={columnMappings}
            setMappings={setColumnMappings}
          />
        </FormGroup>
      )}

      <FormGroup>
        <ParentStorableDataSelector
          label="Location"
          isRequired={true}
          selectedParent={selectedParent}
          onUpdate={setSelectedParent}
          registryId={registryId}
          storableSchema={
            selectedPlateSchema != null
              ? {id: selectedPlateSchema.id, registryId: selectedPlateSchema.registryId}
              : null
          }
          allowNone={false}
        />
      </FormGroup>

      <BarcodeInput
        shouldAutogenerateBarcode={shouldAutogenerateBarcode}
        customBarcode={customBarcode}
        onUpdate={(update) => {
          if (update.shouldAutogenerateBarcode != null) {
            setShouldAutogenerateBarcode(update.shouldAutogenerateBarcode);
          }
          if (update.customBarcode != null) {
            setCustomBarcode(update.customBarcode);
          }
        }}
      />

      <FormGroup>
        <label className="control-label">Project</label>
        <ConnectedStorableSourceSelector
          isWritable={true}
          onChange={setSelectedProjectId}
          registryId={registryId ?? null}
          selectedSourceId={selectedProjectId}
          selectorModalZIndex={1060}
        />
      </FormGroup>

      {BenchlingConfig.STUDIES_ENABLE && (
        <BaseStorablePaneStudyableFormGroup
          studyIds={studyIds}
          onUpdate={(state) => {
            setStudyIds(state.studyIds ?? []);
          }}
        />
      )}
    </Modal>
  );
}

export default PutScratchPlateInInventoryModal;

export function openPutScratchPlateInInventoryModal(props: {
  scratchPlateId: string;
  scratchPlateName: string | null;
  plateSize: PlateSize;
  plateSchemaId: string | null;
}): void {
  const mount = document.createElement('div');
  document.body.appendChild(mount);

  function cleanup(): void {
    ReactDOM.unmountComponentAtNode(mount);
    mount.remove();
  }

  renderToDOM(
    <PutScratchPlateInInventoryModal {...props} onRequestClose={cleanup} onRequestSubmit={cleanup} />,
    mount
  );
}
